import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import type { UserRole } from '@/types/auth';
import { PageSkeleton } from '@/components/ui';

interface AuthGuardProps {
  children: ReactNode;
  /**
   * Required roles to access this route.
   * If not specified, only authentication is required.
   */
  requiredRoles?: UserRole[];
}

/**
 * Route protection component — vereinfacht fuer SRP Direct Auth.
 * Kein Redirect zu Hosted UI mehr, nur Navigation zu /login.
 *
 * @see Spec 0043 Section 6.1 — AuthGuard Redirect aendern
 */
export function AuthGuard({ children, requiredRoles }: AuthGuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();

  // Auth-State wird noch geladen
  if (isLoading) {
    return <PageSkeleton />;
  }

  // Nicht authentifiziert → Login-Seite
  if (!isAuthenticated) {
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // Rollen-Check
  if (requiredRoles && requiredRoles.length > 0) {
    if (!hasRole(requiredRoles)) {
      return <Navigate to="/?error=forbidden" replace />;
    }
  }

  return <>{children}</>;
}
